import React from 'react';
import { Link } from 'react-router-dom';

interface EmptyStateProps {
  title?: string;
  description?: string;
  buttonLabel?: string;
  onButtonClick?: () => void;
  to?: string;
  icon?: React.ReactNode;
  className?: string;
  showAddIcon?: boolean;
  /** Tighter spacing and a smaller illustration, for empty states inside a card. */
  compact?: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  title = 'No data available',
  description,
  buttonLabel,
  onButtonClick,
  to,
  icon,
  className = '',
  showAddIcon = true,
  compact = false,
}) => {
  const ButtonContent = () => (
    <>
      {showAddIcon && <span className="text-lg leading-none font-light">+</span>}
      {buttonLabel}
    </>
  );

  const buttonClasses = "flex items-center gap-2 px-6 py-2.5 button-active text-white font-semibold rounded-lg shadow-lg shadow-blue-500/25 transform transition-all duration-200 hover:-translate-y-0.5 hover:shadow-blue-500/35 active:scale-95 text-sm cursor-pointer";

  return (
    <div className={`flex flex-col items-center justify-center ${compact ? 'py-6' : 'py-16'} px-4 text-center group ${className}`}>
      <div className={`relative ${compact ? 'mb-4' : 'mb-8'} flex justify-center items-center`}>
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-blue-500/5 blur-2xl rounded-full scale-125 group-hover:bg-blue-500/10 transition-all duration-700" />
        
        {icon ? (
          <div className="relative transform transition-transform duration-500 group-hover:scale-110">
            {icon}
          </div>
        ) : (
          <svg
            width={compact ? 120 : 180}
            height={compact ? 94 : 140}
            viewBox="0 0 240 180"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="relative drop-shadow-2xl transform transition-transform duration-500 group-hover:scale-105"
          >
            {/* Main Board Surface */}
            <rect x="40" y="30" width="160" height="120" rx="16" fill="#1E293B" />
            <rect x="42" y="32" width="156" height="116" rx="14" stroke="#334155" strokeWidth="2" />
            
            {/* Kanban Columns */}
            <rect x="55" y="45" width="38" height="85" rx="6" fill="#0F172A" />
            <rect x="101" y="45" width="38" height="85" rx="6" fill="#0F172A" />
            <rect x="147" y="45" width="38" height="85" rx="6" fill="#0F172A" />
 
            {/* Floating Cards / Tasks */}
            <g className="animate-float">
              <rect x="105" y="55" width="30" height="15" rx="4" fill="#3B82F6" fillOpacity="0.8" />
              <rect x="105" y="75" width="30" height="12" rx="3" fill="#1E293B" stroke="#3B82F6" strokeOpacity="0.3" strokeWidth="1" />
            </g>
            
            <g className="animate-float-delayed">
              <rect x="59" y="55" width="30" height="12" rx="3" fill="#64748B" fillOpacity="0.4" />
              <rect x="59" y="72" width="30" height="18" rx="4" fill="#6366F1" fillOpacity="0.6" />
            </g>
 
            <g className="animate-float-slow">
              <rect x="151" y="65" width="30" height="15" rx="4" fill="#10B981" fillOpacity="0.5" />
            </g>
 
            <circle cx="120" cy="115" r="2" fill="#475569" />
            <circle cx="128" cy="115" r="2" fill="#475569" />
            <circle cx="112" cy="115" r="2" fill="#475569" />
          </svg>
        )}
      </div>
 
      <h3 className="text-xl font-bold text-white mb-2 tracking-tight">
        {title}
      </h3>
      
      {description && (
        <p className={`text-neutral-medium max-w-sm ${compact ? 'mb-4' : 'mb-6'} leading-relaxed text-sm`}>
          {description}
        </p>
      )}
 
      {buttonLabel && (
        to ? (
          <Link to={to} className={buttonClasses}>
            <ButtonContent />
          </Link>
        ) : (
          <button onClick={onButtonClick} className={buttonClasses}>
            <ButtonContent />
          </button>
        )
      )}
 
      <style>{`
        @keyframes custom-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-float { animation: custom-float 4s ease-in-out infinite; }
        .animate-float-delayed { animation: custom-float 4.5s ease-in-out infinite 1s; }
        .animate-float-slow { animation: custom-float 5s ease-in-out infinite 0.5s; }
      `}</style>
    </div>
  );
};

export default EmptyState;
